import { Fragment } from 'react';
import { Star, CaretDown } from '@phosphor-icons/react';
import type { SiteContentKey } from '@pms/shared';
import { SearchBar } from './SearchBar';
import { HERO_IMAGE } from '../../data/landing';
import { Reveal } from '../../lib/motion';
import { useSiteContent, resolveStat } from '../../hooks/useSiteContent';

/** The trust chips under the hero search bar. Value + label are super-admin
 *  editable (with hardcoded fallback defaults). */
const HERO_KEYS: SiteContentKey[] = [
  'hero.hotels',
  'hero.cities',
  'hero.verified',
];

/**
 * Editorial hero (#hero) — Warm Editorial, cinematic full-bleed.
 *
 * A full-viewport image with a slow Ken Burns drift, a warm ink scrim for
 * legibility, an oversized serif headline overlaid bottom-left, and a floating
 * search surface. No booking/payment copy — BestAcco is a curated directory you
 * browse and enquire through.
 */
export function EditorialHero() {
  const { data } = useSiteContent();
  const chips = HERO_KEYS.map((key) => resolveStat(data, key));
  return (
    <section
      id="hero"
      aria-labelledby="hero-h1"
      className="relative isolate flex min-h-[640px] w-full flex-col justify-end overflow-hidden bg-ink lg:h-[94vh]"
    >
      {/* Cinematic backdrop */}
      <img
        src={HERO_IMAGE}
        alt="A sunlit boutique hotel at dusk, loungers reflected in a still pool"
        className="ken-burns absolute inset-0 -z-10 h-full w-full object-cover"
        loading="eager"
        decoding="async"
      />
      {/* Warm scrims: bottom lift for the headline, a side wash for depth, a top fade for the nav. */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink via-ink/55 to-ink/15" aria-hidden="true" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-ink/70 via-transparent to-transparent" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 -z-10 h-32 bg-gradient-to-b from-ink/60 to-transparent" aria-hidden="true" />

      {/* Floating editorial credit — brand label, no specific property named so
          it never references demo/hidden listings. */}
      <div className="absolute right-5 top-6 flex items-center gap-3 rounded-full bg-cream/95 px-4 py-2 shadow-lg backdrop-blur sm:right-10 sm:top-10">
        <Star weight="fill" className="h-4 w-4 text-clay" aria-hidden="true" />
        <span className="text-sm">
          <span className="font-serif font-semibold text-ink">Hand-picked stays</span>
          <span className="text-ink-muted"> · Worldwide</span>
        </span>
      </div>

      {/* Headline + search, seated at the bottom */}
      <div className="relative z-10 px-6 pb-16 pt-40 sm:px-10 lg:px-16 lg:pb-20 2xl:px-24">
        <div className="max-w-4xl">
          <Reveal as="h1">
            <span
              id="hero-h1"
              className="block font-serif text-5xl font-black leading-[0.95] tracking-tight text-cream sm:text-7xl xl:text-8xl"
            >
              Stay somewhere
              <br />
              with a{' '}
              <span className="relative inline-block italic text-clay-light">
                story.
                <svg
                  className="absolute -bottom-2 left-0 w-full text-clay"
                  viewBox="0 0 200 12"
                  fill="none"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 9C40 4 80 3 120 5C150 6.5 175 7.5 198 5.5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </span>
          </Reveal>

          <Reveal as="p" delay={120} className="mt-6 max-w-xl text-lg leading-relaxed text-cream/85">
            A curated directory of distinctive, hand-picked hotels: the ones with
            a point of view. Browse, fall for one and book directly with the hotel.
          </Reveal>
        </div>

        <Reveal delay={240} className="mt-9 max-w-5xl">
          <SearchBar />
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 pl-1 text-sm text-cream/75">
            {chips.map((chip, i) => (
              <Fragment key={HERO_KEYS[i]}>
                {i > 0 && (
                  <span className="hidden h-1 w-1 rounded-full bg-clay sm:block" aria-hidden="true" />
                )}
                <span>
                  {chip.value} {chip.label}
                </span>
              </Fragment>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Scroll cue */}
      <div className="pointer-events-none absolute bottom-5 left-1/2 z-10 hidden -translate-x-1/2 lg:block">
        <span className="bob flex h-10 w-10 items-center justify-center rounded-full border border-cream/30 bg-cream/15 text-cream backdrop-blur">
          <CaretDown className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
    </section>
  );
}
